import {
    getSearchParams,
    logDebug,
    logWarn,
    type Maybe,
    parseBoolean,
    ReportableError,
    RequestError,
    StringParseError,
} from '@amazon/vinyl-util'
import { getDeviceInfo } from '@/device/device'
import { getAnonSessionId, getAuth, getAuthToken } from '@/auth/authRef'
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { print } from 'graphql/language'
import type { GraphQLError } from 'graphql/error'
import { graphql } from 'gql.tada'
import { type SanitizedGql, sanitizeGql } from '@/util/gqlUtil'
import { getCacheManager } from '@/offline/offlineCacheManager'
import { getDeviceConfig } from '@/device/deviceConfig'
import { ctx } from '@/util/context'
import type { CacheEntryOptions } from '@amazon/vinyl-cache-manager'
import { blockIdleQueue } from '@/util/idleQueue'
import { sha256 } from '@/util/crypto'
import { instrument } from '@/core/perf'
import { waitForRateLimit, type RateLimitOptions } from '@/util/rateLimit'

//
// Amazon's API from following the https://dashboard.music.amazon.dev/quick-start
//

const GQL_ORIGIN = 'https://gql.music.amazon.dev/'

const { target } = ctx('gql')

/**
 * Optional `?disableApq=true` (or `?disableApq=1`) URL flag for
 * debugging — when set, skip Automatic Persisted Queries entirely
 * and send the full query string on every request. Useful when a
 * Firefly hash collision / mismatch is masking the underlying
 * GraphQL error; without APQ the gateway returns the actual error
 * instead of `PersistedQueryNotFound`.
 */
const APQ_DISABLED = parseBoolean(getSearchParams().get('disableApq'))

/**
 * FireFly API keys
 * used to identify which client we're calling from
 * TODO: Replace these Dragonfly keys with provisioned Greenhornet keys
 *
 * Obtained via https://developer.amazon.com/apps-and-games/login-with-amazon
 */
export const FireflyApiKeys = {
    FIREFLY_IOS_API_KEY: 'amzn1.application.35feb30a280642f7a70e10bea35cda76',
    FIREFLY_ANDROID_API_KEY: 'amzn1.application.e6e1516750ea4b1c8f8820ed9dc848cf',
    FIREFLY_ANONYMOUS_DEBUG_API_KEY: 'amzn1.application.b124893c2c8f4ab2a3130fd015821477',
    FIREFLY_WEB_API_KEY: 'amzn1.application.e1dc16675f9f4c78b31927d5bfd5c229',
    HERMES_API_KEY: 'amzn1.application.587c922830ba47549db8187301736c58',
}

const unexpectedTokenRegex = /Unexpected token at (\d+)/

const graphQlWrapped = (...args: Parameters<typeof graphql>) => {
    try {
        return graphql(...args)
    } catch (error: any) {
        if (typeof error.message === 'string') {
            const match = unexpectedTokenRegex.exec(error.message)
            if (match) {
                const position = parseInt(match[1])
                throw new StringParseError(args[0], position, 'gql')
            }
        }
        throw error
    }
}
Object.assign(graphQlWrapped, {
    ...graphql,
})

export const gql: typeof graphql = graphQlWrapped as typeof graphql

export type GqlFetchOptions = {
    /**
     * If true, the response will be cached to persistence.
     */
    readonly cache?: Maybe<boolean>

    /**
     * If set, will retry once if the error is retryable.
     */
    readonly isRetryableError?: (error: GraphQLError) => boolean

    readonly cacheControlOverrides?: CacheEntryOptions['cacheControlOverrides']

    /**
     * If true, when the gql response contains errors, the fetch request will be rejected instead
     * of returning partial data.
     */
    readonly rejectPartialResponse?: boolean

    /**
     * Override the cache key derived from the operation+variables hash. Useful
     * when you want to invalidate a known query later via {@link invalidateGqlCacheById}.
     */
    readonly cacheId?: string

    /**
     * Override the `x-amzn-feature-flags` header sent to Firefly. When the
     * server-side `api-cache` flag would mask freshly-written data (e.g. on
     * the read that follows a mutation), pass `'use-ups'` to opt out.
     */
    readonly featureFlags?: string

    /**
     * If set, waits for a slot in the named rate-limit bucket before
     * issuing the request. Used to preemptively throttle operations
     * Firefly caps server-side (e.g. NextTrackInQueue, CreateQueue) so
     * we don't get 429s. The bucket id defaults to the operation name.
     */
    readonly rateLimit?: RateLimitOptions
}

const MAX_ATTEMPTS = 2

/**
 * Extracts the operation name from the query.
 */
const operationRegex = /(?:query|mutation|subscription)\s+([A-Za-z]+)/

export function gqlFetch<Result = any, Variables = any>(
    query: TypedDocumentNode<Result, Variables>,
    variables?: Variables,
    options?: GqlFetchOptions
): Promise<SanitizedGql<Result>> {
    const queryString = print(query)
    const [_, operationName] = queryString.match(operationRegex) ?? []
    if (!operationName) logWarn(target, 'Could not infer operation name', query)
    logDebug(target, 'gqlFetch', operationName)
    return instrument('gqlFetch_' + operationName, async () => {
        if (options?.rateLimit) {
            await waitForRateLimit(operationName || 'gqlFetch', options.rateLimit)
        }
        const { deviceId } = getDeviceInfo()
        const cacheManager = getCacheManager()
        const { deviceTypeId } = getDeviceConfig()

        const headers: Record<string, any> = {
            'content-type': 'application/json',
            'x-amzn-device-id': deviceId,
            'x-amzn-device-type': deviceTypeId,
            'x-amzn-feature-flags': options?.featureFlags ?? 'use-ups,api-cache',
            dnt: '1',
        }

        if (getAuth().isSignedIn()) {
            const oAuthToken = await getAuthToken()
            headers.authorization = oAuthToken.startsWith('Atna|')
                ? `AmznMusic ${btoa(
                      JSON.stringify({
                          access_token: oAuthToken,
                          deviceId,
                          deviceTypeId,
                      })
                  )}`
                : `Bearer ${oAuthToken}`
            headers['x-api-key'] = FireflyApiKeys.FIREFLY_WEB_API_KEY
        } else {
            headers['x-api-key'] = FireflyApiKeys.FIREFLY_ANONYMOUS_DEBUG_API_KEY
            headers['music-territory'] = 'US'
            headers['x-amzn-session-id'] = getAnonSessionId()
        }

        const sha256Hash = await sha256(queryString)
        const extensions = APQ_DISABLED
            ? undefined
            : {
                  persistedQuery: {
                      version: 1,
                      sha256Hash,
                  },
              }

        let cacheKey: string | null = null
        if (options?.cache) {
            if (options.cacheId) {
                cacheKey = options.cacheId
            } else {
                const variablesHash = await sha256(JSON.stringify(variables))
                cacheKey = operationName + '-' + sha256Hash + '-' + variablesHash
            }
        }

        let attempts = 0
        // With APQ disabled, the first attempt sends the full query
        // body — there's no hash-only round-trip to retry from.
        let includeQuery = APQ_DISABLED

        while (++attempts <= MAX_ATTEMPTS) {
            // First attempt: hash only (Firefly recognises common
            // queries by hash, saves request size). On a
            // PersistedQueryNotFound, retry with the full query body
            // included alongside the hash so Firefly's APQ layer can
            // register the new query for future requests.
            const body: Record<string, any> = {
                variables,
                operationName,
                ...(extensions ? { extensions } : null),
                ...(includeQuery ? { query: queryString } : null),
            }

            let response: Response

            try {
                // If a cacheKey is set in the options, use a cache-first strategy.
                response = await blockIdleQueue(
                    cacheManager.get(
                        new Request(GQL_ORIGIN, {
                            headers,
                            body: JSON.stringify(body),
                            method: 'POST',
                        }),
                        {
                            cacheKey,
                            cacheControlOverrides: {
                                // When there is a cacheId set, override the GQL cache headers to allow response
                                // caching. If no cacheId is set, this will have no effect and requests will be
                                // network-only.
                                noStore: false,
                                maxAge: 60 * 60,
                                staleWhileRevalidate: 7 * 24 * 60 * 60,
                                ...options?.cacheControlOverrides,
                            },
                            responseIsValid: async (response) => {
                                // Do not cache if there are errors, prefer stale cached responses if there are
                                // errors in the revalidated response.
                                const result = await response.clone().json()
                                return !result.errors
                            },
                        }
                    )
                )
            } catch (error) {
                if (error instanceof RequestError) {
                    if (error.response) {
                        const errorJson = await error.response.json()
                        throw new GqlError(errorJson.errors)
                    } else throw error
                } else {
                    throw error
                }
            }
            const result = await response.json()
            if (result.errors) {
                logWarn(target, printGqlErrors(result.errors))

                const persistErr = result.errors.find(
                    (e: GqlError) => e.message === 'PersistedQueryNotFound'
                )
                if (persistErr && !includeQuery) {
                    includeQuery = true
                    continue
                }

                const shouldRetry: boolean =
                    attempts < MAX_ATTEMPTS &&
                    options?.isRetryableError &&
                    result.errors.find((error: GraphQLError) => options.isRetryableError!(error))

                if (shouldRetry) logDebug(target, 'retrying')
                if (!shouldRetry) {
                    if (options?.rejectPartialResponse) {
                        throw new GqlError(result.errors)
                    }
                    // Not a retryable error, return the data that could be resolved.
                    return sanitizeGql(result.data)
                }
            } else {
                return sanitizeGql(result.data)
            }
        }
        throw new GqlError([])
    })()
}

export class GqlError extends ReportableError {
    get [Symbol.toStringTag](): string {
        return 'GqlError'
    }

    constructor(readonly errors: readonly GraphQLError[]) {
        super(`A service error occurred: ${printGqlErrors(errors)}`)
        Object.setPrototypeOf(this, GqlError.prototype)
    }
}

function printGqlErrors(errors: readonly GraphQLError[]): string {
    return 'gql errors: ' + errors.map((error) => error.message).join('\n')
}

// CacheManager prefixes every cacheKey with this when storing a cache entry,
// so to invalidate by id we have to prefix it the same way.
const CACHE_KEY_PREFIX = 'https://cache/'

/**
 * Invalidate the cached response for a {@link gqlFetch} call that was made with
 * `{ cache: true, cacheId }`. Use this after a mutation to ensure the next
 * read returns fresh data instead of the stale cached payload.
 */
export async function invalidateGqlCacheById(cacheId: string): Promise<void> {
    await getCacheManager().delete(CACHE_KEY_PREFIX + cacheId)
}
